import { ChevronIcon } from "@theme/assets/icons/base";
import { MsButton, NavLink, StrapiLink } from "@theme/index";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { Box, Flex, Text } from "theme-ui";

import type { DrawerActions, DrawerState } from "@theme/index";
import type { NavigationDataProps } from "@theme/types";
import type { ThemeUIStyleObject } from "theme-ui";

interface NavMobileBottomProps {
  navData: NavigationDataProps;
  onClose: () => void;
  NavLinkTextStyles: ThemeUIStyleObject;
  drawerState: DrawerState & DrawerActions;
}

export const NavMobileBottom = ({
  navData,
  onClose,
  NavLinkTextStyles,
  drawerState,
}: NavMobileBottomProps) => {
  const { currentView, showDropdown } = drawerState;

  const {
    isAuthenticated,
    accountAuthenticatedLink,
    loginLink,
    dropdownLinks,
    languages,
    currentLanguage,
  } = navData;

  const handleLanguageClick = () => {
    showDropdown(dropdownLinks.length);
  };

  const { t } = useTranslation("common-phrases");

  return (
    <Flex
      sx={{
        flexDirection: "column",
        gap: "s400",
        alignSelf: "stretch",
        pb: "s1400",
      }}
    >
      {isAuthenticated ? (
        <MsButton
          variant="link"
          onClick={() => showDropdown({ type: "account" })}
          tabIndex={0}
          aria-label="My Account"
          aria-expanded={currentView.type === "account"}
          TrailingIcon={ChevronIcon}
          sx={{
            p: 0,
            span: {
              variant: "text.sansBody03Regular",
              justifyContent: "space-between",
              span: {
                ...NavLinkTextStyles,
              },
              svg: {
                height: "14px",
                color: "grey400",
              },
            },
          }}
        >
          {accountAuthenticatedLink?.mobileLabel ?? accountAuthenticatedLink?.label}
        </MsButton>
      ) : (
        <NavLink
          variant="box"
          to={loginLink.to ?? ""}
          aria-label={loginLink?.mobileLabel}
          onClick={onClose}
        >
          <Text variant="sansBody03Regular" sx={{ ...NavLinkTextStyles }}>
            {loginLink?.mobileLabel ?? loginLink.label}
          </Text>
        </NavLink>
      )}

      {/* Dropdown Links */}
      {dropdownLinks.map((dropdownItem, index) =>
        dropdownItem.links.length > 1 ? (
          <MsButton
            key={index}
            variant="link"
            onClick={() => showDropdown(index)}
            tabIndex={0}
            aria-label={dropdownItem.title}
            aria-expanded={currentView.type === "dropdown" && currentView.index === index}
            TrailingIcon={ChevronIcon}
            sx={{
              p: 0,
              span: {
                variant: "text.sansBody03Regular",
                justifyContent: "space-between",
                span: {
                  ...NavLinkTextStyles,
                },
                svg: {
                  height: "14px",
                  color: "grey400",
                },
              },
            }}
          >
            {dropdownItem.title}
          </MsButton>
        ) : (
          <Box key={dropdownItem.links[0].label} onClick={onClose}>
            <StrapiLink
              key={dropdownItem.links[0].label}
              linkVariant="link"
              ariaLabel={dropdownItem.links[0].label}
              isBlog={navData.isBlog}
              sx={{
                variant: "text.sansBody03Regular",
              }}
              {...dropdownItem.links[0]}
            />
          </Box>
        ),
      )}

      {/* Language Selection Button */}
      {languages.length > 1 && (
        <MsButton
          variant="link"
          onClick={handleLanguageClick}
          tabIndex={0}
          aria-label="Select language"
          aria-expanded={
            currentView.type === "dropdown" && currentView.index === dropdownLinks.length
          }
          TrailingIcon={ChevronIcon}
          sx={{
            p: 0,
            span: {
              variant: "text.sansBody03Regular",
              justifyContent: "space-between",
              span: {
                ...NavLinkTextStyles,
              },
              svg: {
                height: "14px",
                color: "grey400",
              },
            },
          }}
        >
          {t("commonLanguage", { defaultValue: "Language" })}: {currentLanguage.toUpperCase()}
        </MsButton>
      )}
    </Flex>
  );
};
